import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import GroupsIcon from "@mui/icons-material/Groups";
import PaidIcon from "@mui/icons-material/Paid";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import MainContainer from "../../layout/MainContainer";
import { Loader, showError } from "../../constants";
import { useLazyGetDashboardQuery } from "../../services/dashboard";
import { DashboardData } from "../../types/General";
import { Permissions } from "../../types/User";
import useAuth from "../../hooks/useAuth";
import { useNavigate } from "react-router-dom";

type RangeType = "DAILY" | "WEEK" | "MONTH" | "YEAR";
type ViewType = "funnel" | "retention" | "features" | "subscription";

const number = (value: unknown) => Number(value || 0);
const percent = (value: number) => `${value.toFixed(1)}%`;
const money = (value: number) => `$${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const panelSx = { border: "1px solid #dbe4e8", borderRadius: "8px", boxShadow: "none" };

const Analytics = () => {
  const navigate = useNavigate();
  const userData = useAuth();
  const [range, setRange] = useState<RangeType>("MONTH");
  const [view, setView] = useState<ViewType>("funnel");
  const [dashboardData, setDashboardData] = useState<DashboardData>({});
  const [getDashboard, { isLoading, isFetching }] = useLazyGetDashboardQuery();

  const userPermissions = useMemo(
    () =>
      userData?.permissions?.length
        ? JSON.parse(`${userData.permissions}`)
        : [],
    [userData?.permissions]
  );

  const loadDashboard = useCallback(async () => {
    try {
      const response = await getDashboard({ type: range, type2: range }).unwrap();
      if (response?.statusCode === 200) setDashboardData(response.data || {});
    } catch (error: any) {
      showError(error?.data?.message || "Unable to load analytics");
    }
  }, [getDashboard, range]);

  useEffect(() => {
    if (!userData || !userPermissions.length) return;
    const allowed = userPermissions.some(
      (permission: Permissions) =>
        permission?.label === "Reports and Analytics" && permission?.isView
    );
    if (!allowed && userData?.role !== "ADMIN") {
      navigate("/permission-denied", { replace: true });
    }
  }, [navigate, userData, userPermissions]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const total = number(dashboardData.totalCustomers);
  const paid = number(dashboardData.paidCustomers);
  const linked = number(dashboardData.bankAnalytics?.linkedUsers ?? dashboardData.bankAccountsLinked);
  const d7 = dashboardData.retention?.find((item) => item.day === 7)?.rate || 0;
  const signupToPaid = total > 0 ? (paid / total) * 100 : 0;
  const linkRate = total > 0 ? (linked / total) * 100 : 0;
  const trend = useMemo(() => {
    if (Array.isArray(dashboardData.result)) return dashboardData.result;
    return Object.entries(dashboardData.result || {}).map(([name, count]) => ({ name, count }));
  }, [dashboardData.result]);

  const funnel = useMemo(() => {
    const source = dashboardData.activationFunnel || [
      { key: "account", name: "Created account", users: total },
      { key: "bank", name: "Linked bank account", users: linked },
      { key: "trial", name: "Started trial", users: number(dashboardData.trialUsers) },
      { key: "paid", name: "Subscribed", users: paid },
    ];
    return source.map((step, index) => {
      const previous = index === 0 ? step.users : source[index - 1].users;
      const conversion = previous > 0 ? (step.users / previous) * 100 : 0;
      return {
        ...step,
        conversion,
        dropOff: index === 0 ? 0 : Math.max(previous - step.users, 0),
        dropRate: index === 0 || previous <= 0 ? 0 : Math.max(((previous - step.users) / previous) * 100, 0),
      };
    });
  }, [dashboardData.activationFunnel, dashboardData.trialUsers, linked, paid, total]);

  const bottleneck = funnel.slice(1).reduce(
    (worst, step) => (step.dropRate > worst.dropRate ? step : worst),
    { name: "Not enough data", dropRate: 0, dropOff: 0 }
  );
  const featureUsage = dashboardData.featureUsage || [];
  const maxFeatureUsers = Math.max(...featureUsage.map((item) => item.users), 1);

  const metrics = [
    { label: "Total signups", value: total.toLocaleString(), detail: `${number(dashboardData.newUsersMonthly)} this month`, icon: <GroupsIcon /> },
    { label: "Bank link rate", value: percent(linkRate), detail: `${linked} linked users`, icon: <AccountBalanceIcon /> },
    { label: "Day 7 retention", value: percent(d7), detail: `${dashboardData.retention?.find((item) => item.day === 7)?.users || 0} retained users`, icon: <AutoGraphIcon /> },
    { label: "Signup to paid", value: percent(signupToPaid), detail: `${paid} paid customers`, icon: <PaidIcon /> },
    { label: "New users D / W / M", value: `${number(dashboardData.newUsersDaily)} / ${number(dashboardData.newUsersWeekly)} / ${number(dashboardData.newUsersMonthly)}`, detail: "Acquisition velocity", icon: <PersonAddAlt1Icon /> },
  ];

  const renderView = () => {
    if (view === "retention") {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12} lg={8}>
            <Card sx={panelSx}><CardContent>
              <Typography variant="h6" fontWeight={700}>Retention curve</Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>Users active at least N days after signup</Typography>
              <Box height={330}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={dashboardData.retention || []} margin={{ left: -10, right: 18 }}>
                    <defs><linearGradient id="retentionFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#16877d" stopOpacity={0.38} /><stop offset="100%" stopColor="#16877d" stopOpacity={0.03} /></linearGradient></defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e6ecef" />
                    <XAxis dataKey="day" tickFormatter={(day) => `D${day}`} />
                    <YAxis unit="%" domain={[0, 100]} />
                    <Tooltip formatter={(value) => [percent(number(value)), "Retention"]} labelFormatter={(day) => `Day ${day}`} />
                    <Area type="monotone" dataKey="rate" stroke="#16877d" strokeWidth={3} fill="url(#retentionFill)" />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} lg={4}>
            <Card sx={{ ...panelSx, height: "100%" }}><CardContent>
              <Typography variant="h6" fontWeight={700} mb={2}>Retention checkpoints</Typography>
              <Stack spacing={2.25}>
                {(dashboardData.retention || []).map((item) => (
                  <Box key={item.day}>
                    <Stack direction="row" justifyContent="space-between"><Typography fontWeight={600}>Day {item.day}</Typography><Typography fontWeight={700}>{percent(item.rate)}</Typography></Stack>
                    <LinearProgress variant="determinate" value={Math.min(item.rate, 100)} sx={{ mt: 0.75, height: 7, borderRadius: 1, bgcolor: "#e8eff0", "& .MuiLinearProgress-bar": { bgcolor: item.day <= 7 ? "#16877d" : "#e09b45" } }} />
                  </Box>
                ))}
              </Stack>
            </CardContent></Card>
          </Grid>
        </Grid>
      );
    }

    if (view === "features") {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12} lg={7}>
            <Card sx={panelSx}><CardContent>
              <Typography variant="h6" fontWeight={700}>Feature adoption</Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>Unique users with recorded feature output</Typography>
              <Box height={340}><ResponsiveContainer width="100%" height="100%"><BarChart data={featureUsage} layout="vertical" margin={{ left: 34, right: 18 }}><CartesianGrid strokeDasharray="3 3" horizontal={false} /><XAxis type="number" /><YAxis dataKey="name" type="category" width={125} tick={{ fontSize: 12 }} /><Tooltip /><Bar dataKey="users" radius={[0, 4, 4, 0]}>{featureUsage.map((_, index) => <Cell key={index} fill={index === 0 ? "#16877d" : "#4e8791"} />)}</Bar></BarChart></ResponsiveContainer></Box>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} lg={5}>
            <Card sx={{ ...panelSx, height: "100%" }}><CardContent>
              <Typography variant="h6" fontWeight={700} mb={2}>Popularity ranking</Typography>
              <Stack spacing={2}>
                {featureUsage.map((item, index) => (
                  <Stack key={item.name} direction="row" spacing={1.5} alignItems="center">
                    <Typography color="text.secondary" width={20}>{index + 1}</Typography>
                    <Box flex={1}><Stack direction="row" justifyContent="space-between"><Typography fontWeight={600}>{item.name}</Typography><Typography>{item.users}</Typography></Stack><LinearProgress variant="determinate" value={(item.users / maxFeatureUsers) * 100} sx={{ mt: 0.6, height: 5, bgcolor: "#e9eff1", "& .MuiLinearProgress-bar": { bgcolor: "#16877d" } }} /></Box>
                  </Stack>
                ))}
              </Stack>
            </CardContent></Card>
          </Grid>
        </Grid>
      );
    }

    if (view === "subscription") {
      const churn = number(dashboardData.churnRate);
      return (
        <Grid container spacing={2}>
          {[{ label: "Started trial", value: number(dashboardData.trialUsers), color: "#2f6f89" }, { label: "Active paid", value: paid, color: "#16877d" }, { label: "Churned", value: number(dashboardData.churnedCustomers), color: "#bf5555" }, { label: "Churn rate", value: percent(churn), color: "#bf5555" }, { label: "Monthly revenue", value: money(number(dashboardData.monthlyRevenue)), color: "#6d5c8f" }, { label: "Annual run rate", value: money(number(dashboardData.monthlyRevenue) * 12), color: "#6d5c8f" }].map((item) => (
            <Grid item xs={12} sm={6} lg={4} key={item.label}><Card sx={panelSx}><CardContent><Typography color="text.secondary">{item.label}</Typography><Typography variant="h4" fontWeight={750} color={item.color} mt={1}>{item.value}</Typography></CardContent></Card></Grid>
          ))}
          <Grid item xs={12}><Alert severity="info">Cancellation reasons and exit-survey responses will appear after event instrumentation is enabled.</Alert></Grid>
        </Grid>
      );
    }

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          <Card sx={panelSx}><CardContent>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" mb={2}><Box><Typography variant="h6" fontWeight={700}>Activation funnel</Typography><Typography variant="body2" color="text.secondary">Conversion and loss at each measurable customer step</Typography></Box><Chip color="error" variant="outlined" icon={<TrendingDownIcon />} label={`Largest loss: ${bottleneck.name}`} /></Stack>
            <Stack spacing={0}>
              {funnel.map((step, index) => (
                <Box key={step.key} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 70px", md: "190px 1fr 82px 90px" }, gap: 1.5, alignItems: "center", py: 1.2, borderTop: index ? "1px solid #edf1f2" : "none" }}>
                  <Typography fontWeight={650}>{step.name}</Typography>
                  <Box sx={{ display: { xs: "none", md: "block" } }}><LinearProgress variant="determinate" value={total > 0 ? Math.min((step.users / total) * 100, 100) : 0} sx={{ height: 10, borderRadius: 1, bgcolor: "#e8eef0", "& .MuiLinearProgress-bar": { bgcolor: step.dropRate >= 50 ? "#c95f5f" : "#16877d" } }} /></Box>
                  <Typography textAlign="right" fontWeight={700}>{step.users}</Typography>
                  <Typography textAlign="right" color={step.dropRate >= 50 ? "error.main" : "text.secondary"}>{index === 0 ? "Baseline" : `-${percent(step.dropRate)}`}</Typography>
                </Box>
              ))}
            </Stack>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Card sx={{ ...panelSx, mb: 2 }}><CardContent><Typography variant="overline" color="text.secondary">Primary bottleneck</Typography><Typography variant="h5" fontWeight={750} mt={0.5}>{bottleneck.name}</Typography><Typography variant="h3" color="error.main" fontWeight={800} my={1}>{percent(bottleneck.dropRate)}</Typography><Typography color="text.secondary">{bottleneck.dropOff} users lost from the previous step</Typography></CardContent></Card>
          <Card sx={panelSx}><CardContent><Typography variant="h6" fontWeight={700}>Bank connection</Typography><Stack spacing={1.5} mt={2}>{[{ label: "Users linked", value: dashboardData.bankAnalytics?.linkedUsers || linked }, { label: "Accounts linked", value: dashboardData.bankAnalytics?.linkedAccounts || number(dashboardData.bankAccountsLinked) }, { label: "Never linked", value: dashboardData.bankAnalytics?.usersNeverLinked ?? Math.max(total - linked, 0) }, { label: "Accounts per user", value: dashboardData.bankAnalytics?.accountsPerLinkedUser || 0 }].map((item) => <Stack direction="row" justifyContent="space-between" key={item.label}><Typography color="text.secondary">{item.label}</Typography><Typography fontWeight={700}>{item.value}</Typography></Stack>)}</Stack></CardContent></Card>
        </Grid>
      </Grid>
    );
  };

  return (
    <MainContainer>
      <Loader isLoad={isLoading || isFetching} />
      <div className="main_loyout">
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} spacing={2} mb={2.5}>
          <Box><Typography variant="h4" fontWeight={800} color="#102a36">Product Market Fit</Typography><Typography color="text.secondary">Customer journey, activation, retention, and conversion</Typography></Box>
          <FormControl size="small" sx={{ minWidth: 180 }}><Select value={range} onChange={(event: SelectChangeEvent) => setRange(event.target.value as RangeType)}><MenuItem value="DAILY">This week</MenuItem><MenuItem value="WEEK">This month</MenuItem><MenuItem value="MONTH">This year</MenuItem><MenuItem value="YEAR">All years</MenuItem></Select></FormControl>
        </Stack>

        <Grid container spacing={1.5} mb={2}>
          {metrics.map((metric) => <Grid item xs={12} sm={6} lg key={metric.label}><Card sx={{ ...panelSx, height: "100%" }}><CardContent><Stack direction="row" justifyContent="space-between" alignItems="flex-start"><Box><Typography variant="body2" color="text.secondary">{metric.label}</Typography><Typography variant="h5" fontWeight={780} mt={0.7} color="#102a36">{metric.value}</Typography><Typography variant="caption" color="text.secondary">{metric.detail}</Typography></Box><Box color="#16877d">{metric.icon}</Box></Stack></CardContent></Card></Grid>)}
        </Grid>

        <Card sx={{ ...panelSx, mb: 2 }}><CardContent><Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems={{ lg: "center" }}><Box minWidth={180}><Typography fontWeight={700}>Signup trend</Typography><Typography variant="body2" color="text.secondary">{range.toLowerCase()} view</Typography></Box><Box height={140} flex={1} width="100%"><ResponsiveContainer width="100%" height="100%"><AreaChart data={trend} margin={{ left: -25, right: 8, top: 6 }}><defs><linearGradient id="signupFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2f8290" stopOpacity={0.35} /><stop offset="100%" stopColor="#2f8290" stopOpacity={0.02} /></linearGradient></defs><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} /><Tooltip /><Area type="monotone" dataKey="count" stroke="#2f8290" strokeWidth={2} fill="url(#signupFill)" /></AreaChart></ResponsiveContainer></Box></Stack></CardContent></Card>

        <Tabs value={view} onChange={(_, value: ViewType) => setView(value)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2, borderBottom: "1px solid #dbe4e8", "& .MuiTab-root": { textTransform: "none", fontWeight: 650 } }}><Tab value="funnel" label="Activation funnel" /><Tab value="retention" label="Retention" /><Tab value="features" label="Feature usage" /><Tab value="subscription" label="Subscriptions" /></Tabs>
        {renderView()}

        <Alert severity="warning" sx={{ mt: 2 }}>Visitor attribution, Plaid attempt/error details, Money Leak report events, user timelines, feedback trends, and experiments require app event instrumentation before reliable metrics can be shown.</Alert>
      </div>
    </MainContainer>
  );
};

export default Analytics;
